import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useEffect, useRef } from "react";
import { useLocalStorage } from "usehooks-ts";
import type { CodesmithSidebarVariant } from "./sidebarVariant";
export const MIN_SIDEBAR_WIDTH = 240;
export const MAX_SIDEBAR_WIDTH = 480;

/**
 * Key/default are exported so the loading skeleton (SidebarSkeleton) can
 * render at the width the real sidebar will restore.
 */
export const SESSION_SIDEBAR_WIDTH_KEY = "assistantSidebarWidth";
export const DEFAULT_SESSION_SIDEBAR_WIDTH = 330;
export const REDESIGN_SIDEBAR_WIDTH_KEY = "codesmithSidebarRedesignWidth";
export const REDESIGN_MIN_SIDEBAR_WIDTH = 240;
export const DEFAULT_REDESIGN_SIDEBAR_WIDTH = 240;
export interface SidebarWidthConfig {
  storageKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
}
export function sidebarWidthConfig(variant: CodesmithSidebarVariant): SidebarWidthConfig {
  return variant === "redesign" ? {
    storageKey: REDESIGN_SIDEBAR_WIDTH_KEY,
    defaultWidth: DEFAULT_REDESIGN_SIDEBAR_WIDTH,
    minWidth: REDESIGN_MIN_SIDEBAR_WIDTH,
    maxWidth: MAX_SIDEBAR_WIDTH
  } : {
    storageKey: SESSION_SIDEBAR_WIDTH_KEY,
    defaultWidth: DEFAULT_SESSION_SIDEBAR_WIDTH,
    minWidth: MIN_SIDEBAR_WIDTH,
    maxWidth: MAX_SIDEBAR_WIDTH
  };
}
export function clampSidebarWidth(width: number, {
  minWidth,
  maxWidth
}: SidebarWidthConfig): number {
  return Math.min(maxWidth, Math.max(minWidth, width));
}
export function useResizableSidebar(t0) {
  const $ = _c(15);
  const variantOrConfig = t0 === undefined ? "legacy" : t0;
  let t1;
  if ($[0] !== variantOrConfig) {
    t1 = typeof variantOrConfig === "string" ? sidebarWidthConfig(variantOrConfig) : variantOrConfig;
    $[0] = variantOrConfig;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const config = t1;
  const [storedWidth, setSidebarWidth] = useLocalStorage(config.storageKey, config.defaultWidth);
  let t2;
  if ($[2] !== config || $[3] !== storedWidth) {
    t2 = clampSidebarWidth(storedWidth, config);
    $[2] = config;
    $[3] = storedWidth;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const sidebarWidth = t2;
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef(null);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e => {
      if (e.button !== 0) {
        return;
      }
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      setIsResizing(true);
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleResizePointerDown = t3;
  const {
    minWidth,
    maxWidth
  } = config;
  let t4;
  let t5;
  if ($[6] !== isResizing || $[7] !== maxWidth || $[8] !== minWidth || $[9] !== setSidebarWidth) {
    t4 = () => {
      if (!isResizing) {
        return;
      }
      let rafId = null;
      let pendingWidth = null;
      const handlePointerUp = () => {
        if (pendingWidth !== null) {
          setSidebarWidth(pendingWidth);
        }
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        setIsResizing(false);
      };
      const handlePointerMove = e_0 => {
        if ((e_0.buttons & 1) === 0) {
          handlePointerUp();
          return;
        }
        if (!sidebarRef.current) {
          return;
        }
        const containerRect = sidebarRef.current.getBoundingClientRect();
        const newWidth = e_0.clientX - containerRect.left;
        pendingWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
        if (rafId === null) {
          rafId = requestAnimationFrame(() => {
            if (pendingWidth !== null) {
              setSidebarWidth(pendingWidth);
            }
            rafId = null;
          });
        }
      };
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
      document.addEventListener("pointercancel", handlePointerUp);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "col-resize";
      return () => {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        document.removeEventListener("pointercancel", handlePointerUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    };
    t5 = [isResizing, setSidebarWidth, minWidth, maxWidth];
    $[6] = isResizing;
    $[7] = maxWidth;
    $[8] = minWidth;
    $[9] = setSidebarWidth;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  let t6;
  if ($[12] !== isResizing || $[13] !== sidebarWidth) {
    t6 = {
      sidebarWidth,
      isResizing,
      sidebarRef,
      handleResizePointerDown
    };
    $[12] = isResizing;
    $[13] = sidebarWidth;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}